/**
 * Hub Selection Page
 *
 * Landing page after login that displays available hubs.
 * Uses the same background as the ATLAS Loader app.
 */

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../shared/contexts/AuthContext';
import { ArrowRight, LogOut, Truck } from 'lucide-react';

const loaderBg = 'radial-gradient(circle at top left, rgba(37,99,235,0.18), transparent 55%), radial-gradient(circle at 20% 85%, rgba(14,116,144,0.16), transparent 48%), linear-gradient(135deg, #f8fafc 0%, #eef2f7 40%, #f5f7fb 100%)';

const HubSelection = (): JSX.Element => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleTransportPlanningClick = (): void => {
    navigate('/hub/truck-router/create-transport');
  };

  const handleLogout = async (): Promise<void> => {
    await logout();
    navigate('/login');
  };

  const displayName = user?.displayName || user?.email || 'User';
  const initials = displayName
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{ background: loaderBg }}
    >
      <div className="max-w-lg w-full relative z-10">
        {/* Header */}
        <div className="text-center mb-10 space-y-5">
          {/* Logo */}
          <div className="flex justify-center">
            <img
              src="/willbanks-logo.png"
              alt="Willbanks"
              className="h-16 w-auto"
            />
          </div>

          {/* Title */}
          <div>
            <h1 className="text-3xl tablet:text-4xl font-bold text-slate-900 font-heading tracking-tight">
              Transport Management
            </h1>
            <p className="text-sm text-slate-500 mt-2">Select a hub to get started</p>
          </div>

          {/* User Info */}
          <div className="inline-flex items-center gap-3 bg-white border border-slate-200 rounded-2xl px-5 py-3 shadow-sm">
            <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-white text-xs font-bold">
              {initials}
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-slate-900">{displayName}</p>
              <p className="text-xs text-slate-500">{user?.role || 'Unknown'}</p>
            </div>
            <div className="h-8 w-px bg-slate-200"></div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-xl transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign Out
            </button>
          </div>
        </div>

        {/* Transport Planning Card */}
        <div className="flex justify-center mb-10">
          <button
            onClick={handleTransportPlanningClick}
            className="group relative bg-white border border-slate-200 rounded-2xl p-6 tablet:p-8 text-left
              transition-all duration-200 max-w-md w-full shadow-sm
              hover:shadow-lg hover:border-slate-300 hover:-translate-y-1 cursor-pointer"
          >
            {/* Status Badge */}
            <div className="absolute top-4 right-4">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-green-50 text-green-700 border border-green-200">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                Active
              </span>
            </div>

            {/* Icon */}
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl mb-5 bg-slate-100 group-hover:bg-slate-200 transition-colors">
              <Truck className="w-7 h-7 text-slate-700" />
            </div>

            {/* Hub Name */}
            <h3 className="text-xl font-bold text-slate-900 font-heading mb-2">
              Transport Planning
            </h3>

            {/* Description */}
            <p className="text-sm text-slate-500 mb-4 leading-relaxed">
              Perform day-to-day transport planning activities
            </p>

            {/* Features */}
            <ul className="space-y-1.5 mb-5">
              <li className="text-sm text-slate-600 flex items-center gap-2">
                <span className="w-1 h-1 bg-slate-400 rounded-full"></span>
                Plan Transports
              </li>
              <li className="text-sm text-slate-600 flex items-center gap-2">
                <span className="w-1 h-1 bg-slate-400 rounded-full"></span>
                Edit Shipments
              </li>
            </ul>

            {/* Action */}
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 group-hover:text-slate-900 transition-colors">
              <span>Open Hub</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-2" />
            </div>
          </button>
        </div>

        {/* Powered By */}
        <div className="flex items-center justify-center gap-3 pt-6 border-t border-slate-200">
          <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Powered by</span>
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5 shadow-sm">
            <img
              src="/invera-logo.svg"
              alt="Invera"
              className="w-4 h-4"
              style={{ imageRendering: '-webkit-optimize-contrast' as const }}
            />
            <span className="text-[10px] font-bold text-slate-700 tracking-wider">INVERA ERP</span>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-[10px] text-slate-400">
            © {new Date().getFullYear()} Willbanks Metals • Transport Management v1.0
          </p>
        </div>
      </div>
    </div>
  );
};

export default HubSelection;
